import { fmt0, fmt1 } from "@/components/nutrition/NutritionParts";

const GREEN = "#15803d";

/* dry food — a measuring scoop that fills to the portion */
function Scoop({ shown, max, userVal, revealed }) {
  const top = 16, bottom = 128;
  const yFor = (f) => bottom - Math.max(0, Math.min(1, f)) * (bottom - top);
  const tFill = max > 0 ? shown / max : 0;
  const uFill = userVal != null && max > 0 ? userVal / max : null;
  return (
    <svg viewBox="0 0 130 150" className="w-20 h-24 shrink-0">
      <clipPath id="scoopClip"><path d="M32 16 L98 16 L90 128 L40 128 Z" /></clipPath>
      <g clipPath="url(#scoopClip)">
        <rect x="20" y={yFor(tFill)} width="100" height={bottom} fill={GREEN} opacity="0.8" />
        {revealed && uFill != null && (
          <line x1="20" x2="118" y1={yFor(uFill)} y2={yFor(uFill)} stroke="#dc2626" strokeWidth="2.5" strokeDasharray="5 3" />
        )}
      </g>
      <path d="M32 16 L98 16 L90 128 L40 128 Z" fill="none" stroke="#94a3b8" strokeWidth="2" />
      {[0.25, 0.5, 0.75].map((g) => (
        <line key={g} x1="86" x2="96" y1={yFor(g)} y2={yFor(g)} stroke="#94a3b8" strokeWidth="1.5" />
      ))}
    </svg>
  );
}

/* wet food — a row of cans filled to the number of cans */
function Cans({ shown }) {
  const n = Math.max(1, Math.ceil(shown || 0));
  return (
    <div className="flex items-end gap-1.5 shrink-0">
      {Array.from({ length: n }).map((_, i) => {
        const fill = Math.max(0, Math.min(1, (shown || 0) - i));
        return (
          <svg key={i} viewBox="0 0 40 60" className="w-8 h-12">
            <clipPath id={`can${i}`}><rect x="6" y="10" width="28" height="44" rx="4" /></clipPath>
            <g clipPath={`url(#can${i})`}>
              <rect x="6" y={54 - fill * 44} width="28" height={fill * 44} fill="#b45309" opacity="0.8" />
            </g>
            <rect x="6" y="10" width="28" height="44" rx="4" fill="none" stroke="#94a3b8" strokeWidth="2" />
            <rect x="4" y="5" width="32" height="8" rx="3" fill="#cbd5e1" />
          </svg>
        );
      })}
    </div>
  );
}

/* liquid diet — a graduated syringe */
function LiquidBar({ shown, max }) {
  const f = max > 0 ? Math.min(1, shown / max) : 0;
  return (
    <svg viewBox="0 0 170 50" className="w-32 h-10 shrink-0">
      <rect x="8" y="14" width="124" height="22" rx="3" fill="#f1f5f9" stroke="#94a3b8" strokeWidth="2" />
      <rect x="8" y="14" width={124 * f} height="22" fill="#0ea5a4" opacity="0.75" />
      {[0.25, 0.5, 0.75].map((g) => (
        <line key={g} x1={8 + 124 * g} x2={8 + 124 * g} y1="14" y2="22" stroke="#94a3b8" strokeWidth="1.5" />
      ))}
      <rect x="132" y="18" width="6" height="14" fill="#64748b" />
      <rect x="138" y="20" width="18" height="10" rx="2" fill="#cbd5e1" />
    </svg>
  );
}

// Shows the portion as a real-world vessel. Before reveal it mirrors the learner's
// own entry (no spoiler); after reveal it shows the correct portion + their marker.
export function PortionVisualiser({ mode, target, value, revealed }) {
  const userNum = parseFloat(value);
  const u = isNaN(userNum) ? null : userNum;
  const shown = revealed ? target : u ?? 0;
  const unit = mode === "dry" ? "g" : mode === "wet" ? "cans" : "mL";
  const fmtN = (n) => (mode === "wet" ? fmt1(n) : fmt0(n));
  const max = mode === "wet" ? Math.max(1, Math.ceil(target)) : target * 1.6;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 flex items-center gap-4" data-testid="portion-visualiser">
      {mode === "dry" && <Scoop shown={shown} max={max} userVal={u} revealed={revealed} />}
      {mode === "wet" && <Cans shown={shown} />}
      {mode === "liquid" && <LiquidBar shown={shown} max={max} />}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          {revealed ? "Correct portion" : "Your portion"}
        </div>
        <div className="text-2xl font-bold font-mono-clinical" style={{ color: revealed ? GREEN : "#334155" }} data-testid="portion-visualiser-amount">
          {u == null && !revealed ? "—" : fmtN(shown)}
          <span className="text-sm text-slate-500 ml-1">{unit}/day</span>
        </div>
        {revealed && u != null && (
          <div className="text-[11px] mt-0.5 text-slate-600">
            Your answer: <span className="font-mono-clinical font-semibold">{fmtN(u)} {unit}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Downward weight-loss projection chart for weight-loss cases.
export function WeightTrendChart({ proj }) {
  if (!proj) return null;
  const { current, ideal, weeks } = proj;
  const W = 280, H = 120, padL = 36, padB = 24, padT = 12, padR = 14;
  const maxW = current * 1.03, minW = ideal * 0.97;
  const x = (wk) => padL + (wk / weeks) * (W - padL - padR);
  const y = (kg) => padT + (1 - (kg - minW) / (maxW - minW || 1)) * (H - padT - padB);
  const pts = Array.from({ length: weeks + 1 }).map((_, wk) => {
    const kg = current - (current - ideal) * (wk / weeks);
    return `${x(wk).toFixed(1)},${y(kg).toFixed(1)}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-sm mt-3" data-testid="weight-trend-chart">
      {/* axes */}
      <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#cbd5e1" strokeWidth="1" />
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#cbd5e1" strokeWidth="1" />
      {/* ideal target line */}
      <line x1={padL} y1={y(ideal)} x2={W - padR} y2={y(ideal)} stroke="#15803d" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7" />
      <text x={W - padR} y={y(ideal) - 4} textAnchor="end" className="fill-emerald-700" style={{ fontSize: 9, fontWeight: 700 }}>ideal {fmt1(ideal)} kg</text>
      {/* trend */}
      <polyline points={pts} fill="none" stroke="#0047AB" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(0)} cy={y(current)} r="3.5" fill="#0047AB" />
      <circle cx={x(weeks)} cy={y(ideal)} r="3.5" fill="#15803d" />
      <text x={x(0)} y={y(current) - 6} className="fill-slate-700" style={{ fontSize: 9, fontWeight: 700 }}>{fmt1(current)} kg</text>
      {/* axis labels */}
      <text x={padL} y={H - 8} className="fill-slate-400" style={{ fontSize: 9 }}>wk 0</text>
      <text x={W - padR} y={H - 8} textAnchor="end" className="fill-slate-400" style={{ fontSize: 9 }}>wk {weeks}</text>
    </svg>
  );
}
